import { useSearch } from '@/common/apis/services/search/search';
import Alert from '@/common/components/atom/alert';
import Avatar from '@/common/components/atom/avatar';
import Button from '@/common/components/atom/button';
import Loading from '@/common/components/atom/loading';
import Modal from '@/common/components/atom/modal';
import ChevronIcon from '@/common/components/icons/chevron';
import useModal from '@/common/hooks/useModal';
import { splunkInstance } from '@/common/services/splunk';
import { getImageUrl } from '@/common/utils/getImageUrl';
import { doctorSlugFromSearchHit, useRaviCardRate } from '@/modules/profile/hooks/useDoctorRateDetails';
import SearchCard from '@/modules/search/components/card/card';
import random from 'lodash/random';
import { useMemo } from 'react';
import getConfig from 'next/config';
const { publicRuntimeConfig } = getConfig();

/** `inline`: flow in the page (app home) instead of floating above the bottom nav. */
export const OnlineVisitPromote = ({ inline = false }: { inline?: boolean } = {}) => {
  const { handleOpen, modalProps } = useModal();

  const searchData = useSearch({
    route: decodeURIComponent(`ir/general-practitioner`),
    query: {
      turn_type: 'consult',
      limit: 3,
    },
  });

  const substituteDoctor = useMemo(() => searchData.data?.search?.result?.[random(0, 2)] ?? {}, [searchData.data]);
  const raviRate = useRaviCardRate(doctorSlugFromSearchHit(substituteDoctor));

  const handleOpenSubstituteDoctorModal = () => {
    splunkInstance('search').sendEvent({
      group: 'home_visit_online_doctor_recommendation',
      type: 'home_visit_online_doctor_recommendation-open-modal',
    });
    handleOpen();
  };

  const handleClickDcotorCardDoctor = ({ url }: { url: string }) => {
    splunkInstance('search').sendEvent({
      group: 'home_visit_online_doctor_recommendation',
      type: 'home_visit_online_doctor_recommendation-click-doctor-card',
      event: {
        slug: substituteDoctor?.slug || url.split('/').filter(Boolean).pop() || '',
      },
    });
    location.assign(url);
  };

  const handleClickMoreDoctors = () => {
    splunkInstance('search').sendEvent({
      group: 'home_visit_online_doctor_recommendation',
      type: 'home_visit_online_doctor_recommendation-click-more-doctors',
    });
    // Don't close the sheet first: its history.back() would cancel this navigation on mobile.
    location.assign('/consult?from_recommend_section=1');
  };

  return (
    <>
      <div className={inline ? 'w-full' : 'fixed bottom-[5.5rem] md:flex justify-center w-full px-4 z-10'}>
        <Button
          onClick={handleOpenSubstituteDoctorModal}
          className={
            inline
              ? 'h-16 w-full justify-between rounded-2xl border-slate-200 bg-white text-sm font-semibold text-slate-800'
              : 'rounded-full font-semibold text-[.8rem] bg-[#f9f9f9] border-[#e1e0e0] text-slate-700/80  w-full md:w-auto justify-between'
          }
          variant="secondary"
        >
          {inline ? (
            <span className="flex flex-col items-start gap-0.5">
              <span>می‌خواهم همین الان با پزشک گفت‌وگو کنم</span>
              <span className="flex items-center gap-1 text-[11px] font-normal text-slate-500">
                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                پزشکان آنلاین همین حالا پاسخ می‌دهند
              </span>
            </span>
          ) : (
            'می‌خواهم همین الان با پزشک گفت‌وگو کنم'
          )}
          <div className="flex items-center gap-3">
            <div className={inline ? 'hidden items-center min-[360px]:flex' : 'flex items-center '}>
              {searchData.data?.search?.result?.map((item: any) => (
                <Avatar
                  key={item?.id}
                  src={getImageUrl(item.image, publicRuntimeConfig.CDN_BASE_URL)}
                  className="-ml-2 border-2 border-white"
                  width={25}
                  height={25}
                ></Avatar>
              ))}
            </div>
            <ChevronIcon dir="left" className="mr-1" />
          </div>
        </Button>
      </div>

      <Modal name="online-visit-promote" bodyClassName="p-3" title="" {...modalProps}>
        {(searchData.isLoading || !substituteDoctor?.actions?.[0]?.url) && (
          <div className="flex justify-center w-full">
            <Loading className="w-8 h-8 my-8 " />
          </div>
        )}
        {searchData.isSuccess && substituteDoctor?.actions?.[0]?.url && (
          <div className="flex flex-col space-y-2 mb-2">
            <Alert severity="success" className="p-3 text-green-700 text-sm font-medium">
              بدون خروج از منزل، آنلاین ویزیت شوید.
            </Alert>
            <div onClick={() => handleClickDcotorCardDoctor({ url: substituteDoctor.actions[0].url })}>
              <SearchCard
                avatarSize="lg"
                baseInfo={{
                  displayName: substituteDoctor.title,
                  expertise: substituteDoctor.display_expertise,
                  experience: substituteDoctor.experience,
                  isVerify: true,
                  avatar: substituteDoctor.image,
                  rate: raviRate,
                  isOnline: true,
                }}
                type="doctor"
                className="shadow-none !py-0 lg:!py-0 cursor-pointer"
                actions={[
                  {
                    text: `گفت‌وگو با ${substituteDoctor.title}`,
                    outline: false,
                    description: '',
                  },
                ]}
              />
            </div>
          </div>
        )}
        <Button block size="sm" className="text-xs opacity-70" variant="text" onClick={handleClickMoreDoctors}>
          مشاهده سایر پزشکان آنلاین
        </Button>
      </Modal>
    </>
  );
};

export default OnlineVisitPromote;
